import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaInTextAuthors,
  apaNameInitialsFirst,
  type CitationParts,
  chicagoShortAuthors,
  mlaAuthors,
  mlaInTextAuthors,
  naturalName,
  type PersonName,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  enDashRange,
  mlaPageLocator,
  pageEnds,
} from "@/lib/tool-kit/citation-sources-books/common";
import { titleCase } from "@/lib/tool-kit/citation-sources-more/common";
import { editionLabel } from "@/lib/tool-kit/citation-sources-more/lab-manual-citation-generator";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mlaPageRange,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  type MediaLabels,
  type MediaValues,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";
import {
  chicagoBibliographyContributors,
  chicagoNoteContributors,
  chicagoPageRange,
  mlaContributors,
  publisherName,
} from "@/lib/tool-kit/special-sources/common";

/*
 * A Shakespeare play or the sonnets, in an edition of its own or inside a collected
 * works, in APA 7, MLA 9 and Chicago 18, with the act.scene.line (or sonnet and line)
 * in-text citation.
 *
 * Sources checked (October 2026). The style manuals are not readable by automated
 * tools; each rule below was read on the MLA Style Center or in library guides that
 * reproduce the manual, and is marked where it is a convention:
 * - MLA 9 (MLA Style Center, "play with verse and prose", "epilogues in Shakespeare
 *   plays", "edition without line numbers", "citing sonnets from a sequence"): line
 *   numbers follow act and scene, the numbers written 5.1.323, with a range as in
 *   325–26 and in-text "(Shakespeare 2.4.263-64)"; a play is italic; a stand-alone
 *   edition is a book — "Shakespeare, William. Henry V. Edited by Gary Taylor, Oxford
 *   UP, 1982." — and a play in a collected works is "Shakespeare, William. Macbeth.
 *   The Plays of William Shakespeare, vol. 10, edited by George Seeves et al., T.
 *   Davison, 1813, pp. 11–295." The Style Center's Folger example names the series
 *   before the publisher: "Folger Shakespeare Library / Washington Square Press".
 *   Sonnets: name the sonnet by number in the prose or in the parentheses, "(sonnet
 *   137, lines 1–2)", with one Works Cited entry for the whole sequence. The standard
 *   abbreviations for play titles (Ham., Mac.) are in the Handbook; library guides
 *   differ on their punctuation, so this tool takes the abbreviation as typed and
 *   never supplies one.
 * - Chicago (CMOS 14.153 in the 17th edition, read through the Notre Dame Australia
 *   and Purdue guides): "William Shakespeare, The Tempest, ed. Alden T. Vaughan and
 *   Virginia Mason Vaughan (London: Bloomsbury, 2011), 1.1.20-26." with the first note
 *   adding "References are to act, scene, and line."; short note "Shakespeare, A
 *   Midsummer Night's Dream, 3.2.342." The bibliography carries the full edition:
 *   "Shakespeare, William. The Tempest. Edited by Alden T. Vaughan and Virginia Mason
 *   Vaughan. Arden Shakespeare, 3rd ser. Bloomsbury, 2011." Chicago 18 drops the place
 *   of publication for books after 1900. A play inside a collected works, and the
 *   sonnet locator, follow Chicago's general pattern for a part of a book — a
 *   convention, not a published Shakespeare example.
 * - APA 7: a classic text takes two dates, the original and the edition used, in the
 *   reference "(Original work published 1623)" and in text "(Shakespeare, 1623/2022)";
 *   the editor goes in parentheses after the title (B. A. Mowat & P. Werstine, Eds.),
 *   and an act.scene.line locator ends the in-text citation (University of Portsmouth
 *   guide). A series is not part of an APA reference.
 */

export const SHAKESPEARE_FIELDS = {
  title: "title",
  editors: "editors",
  collection: "collection",
  edition: "edition",
  volume: "volume",
  series: "series",
  publisher: "publisher",
  year: "year",
  originalYear: "originalYear",
  pages: "pages",
  url: "url",
  act: "act",
  scene: "scene",
  sonnet: "sonnet",
  lines: "lines",
  abbreviation: "abbreviation",
} as const;

const F = SHAKESPEARE_FIELDS;

/** A play, or the sonnets. */
export type ShakespeareWork = "play" | "sonnets";

/** An edition of its own, or a play or the sonnets inside a collected works. */
export type ShakespeareBook = "own" | "collected";

export interface ShakespeareLabels extends MediaLabels {
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Work {
  kind: ShakespeareWork;
  book: ShakespeareBook;
  title: string;
  editors: PersonName[];
  collection: string;
  edition: string;
  volume: string;
  series: string;
  publisher: string;
  year: string;
  originalYear: string;
  pages: string;
  url: string;
  act: string;
  scene: string;
  sonnet: string;
  lines: string;
  abbreviation: string;
}

const AUTHOR: PersonName = { given: ["William"], family: "Shakespeare" };

const RE_URL_SCHEME = /^https?:\/\//i;
const RE_EN_DASH = /–/g;
const RE_TRAILING_PERIOD = /\.$/;
const RE_ROMAN_LOCATOR = /^[ivxlc]+$/i;
const ROMAN_VALUES: Record<string, number> = {
  i: 1,
  v: 5,
  x: 10,
  l: 50,
  c: 100,
};

/** "III" or "iii" → "3"; Arabic numbers and anything else pass through unchanged. */
export function arabicLocator(text: string): string {
  const trimmed = text.trim();
  if (!RE_ROMAN_LOCATOR.test(trimmed)) {
    return trimmed;
  }
  const letters = trimmed.toLowerCase();
  let total = 0;
  for (let index = 0; index < letters.length; index++) {
    const value = ROMAN_VALUES[letters[index]];
    const next = ROMAN_VALUES[letters[index + 1] ?? ""] ?? 0;
    total += value < next ? -value : value;
  }
  return String(total);
}

function readWork(
  kind: ShakespeareWork,
  book: ShakespeareBook,
  values: MediaValues
): Work {
  const collected = book === "collected";
  const url = get(values, F.url);
  return {
    kind,
    book,
    title: capitalizeSubtitle(get(values, F.title)),
    editors: parsePeople(get(values, F.editors)),
    collection: collected ? get(values, F.collection) : "",
    edition: editionLabel(get(values, F.edition)),
    volume: collected ? get(values, F.volume) : "",
    series: collected ? "" : get(values, F.series),
    publisher: get(values, F.publisher),
    year: get(values, F.year),
    originalYear: get(values, F.originalYear),
    pages: collected ? get(values, F.pages) : "",
    url: url && !RE_URL_SCHEME.test(url) ? `https://${url}` : url,
    act: kind === "play" ? arabicLocator(get(values, F.act)) : "",
    scene: kind === "play" ? arabicLocator(get(values, F.scene)) : "",
    sonnet: kind === "sonnets" ? get(values, F.sonnet) : "",
    lines: get(values, F.lines),
    abbreviation: get(values, F.abbreviation),
  };
}

/* ------------------------------------------------------------------ locators */

/** "3.1.64–65" with an en dash, or "3.1" without lines. */
function dotted(work: Work, range: (text: string) => string): string {
  return [work.act, work.scene, work.lines ? range(work.lines) : ""]
    .filter(Boolean)
    .join(".");
}

/** MLA shortens the second number of a range above 99: 263-64. */
function mlaLines(text: string): string {
  const [from, to] = pageEnds(text);
  if (to === "") {
    return from;
  }
  return mlaPageRange(from, to).replace(RE_EN_DASH, "-");
}

function chicagoLines(text: string): string {
  const [from, to] = pageEnds(text);
  return to ? chicagoPageRange(from, to) : from;
}

function sonnetPart(
  work: Work,
  label: string,
  range: (text: string) => string
) {
  const number = work.sonnet ? `${label} ${work.sonnet}` : "";
  const lines = work.lines
    ? `${work.lines.includes("–") || work.lines.includes("-") ? "lines" : "line"} ${range(work.lines)}`
    : "";
  return [number, lines].filter(Boolean).join(", ");
}

/** The pinpoint that follows the author in each style's in-text citation. */
function pinpoint(work: Work, style: "apa" | "mla" | "chicago"): string {
  if (work.kind === "play") {
    if (style === "mla") {
      return dotted(work, mlaLines);
    }
    return dotted(work, style === "apa" ? enDashRange : chicagoLines);
  }
  if (style === "mla") {
    return sonnetPart(work, "sonnet", mlaLines);
  }
  return sonnetPart(
    work,
    style === "apa" ? "Sonnet" : "sonnet",
    style === "apa" ? enDashRange : chicagoLines
  );
}

/* ------------------------------------------------------------------ helpers */

function lowerFirst(text: string): string {
  return `${text.charAt(0).toLowerCase()}${text.slice(1)}`;
}

function dateOf(text: string): string {
  return text.trim();
}

function pagesRange(text: string, style: "apa" | "chicago"): string {
  const [from, to] = pageEnds(text);
  if (to === "") {
    return from;
  }
  return style === "apa" ? `${from}–${to}` : chicagoPageRange(from, to);
}

/* ---------------------------------------------------------------------- APA */

/** "A", "A & B", "A, B, & C" in initials-first form, for the In clause. */
function apaEditorList(names: PersonName[]): string {
  const formatted = names.map(apaNameInitialsFirst);
  if (formatted.length <= 1) {
    return formatted.join("");
  }
  if (formatted.length === 2) {
    return `${formatted[0]} & ${formatted[1]}`;
  }
  return `${formatted.slice(0, -1).join(", ")}, & ${formatted.at(-1)}`;
}

function apaEditorRole(work: Work): string {
  return work.editors.length > 1 ? "Eds." : "Ed.";
}

function apaYearText(work: Work): string {
  const year = dateOf(work.year) || "n.d.";
  return work.originalYear ? `${work.originalYear}/${year}` : year;
}

function apaReference(work: Work): CitationParts {
  const year = dateOf(work.year) || "n.d.";
  const original = work.originalYear
    ? ` (Original work published ${work.originalYear})`
    : "";
  const publisher = work.publisher
    ? ` ${withPeriod(publisherName(work.publisher, "apa"))}`
    : "";
  const url = work.url ? ` ${work.url}` : "";
  const head = `Shakespeare, W. (${year}). `;
  if (work.book === "collected") {
    const pages = work.pages ? `pp. ${pagesRange(work.pages, "apa")}` : "";
    const volume = work.volume ? `Vol. ${work.volume}` : "";
    const inner = [work.edition, volume, pages].filter(Boolean).join(", ");
    const editors = work.editors.length
      ? `${apaEditorList(work.editors)} (${apaEditorRole(work)}), `
      : "";
    return tidyParts([
      plain(`${head}${work.title}${closer(work.title)} In ${editors}`),
      italic(work.collection),
      plain(`${inner ? ` (${inner})` : ""}.${publisher}${url}${original}`),
    ]);
  }
  const editors = work.editors.length
    ? `${apaEditorList(work.editors)}, ${apaEditorRole(work)}`
    : "";
  const inner = [editors, work.edition].filter(Boolean).join("; ");
  return tidyParts([
    plain(head),
    italic(work.title),
    plain(`${inner ? ` (${inner})` : ""}.${publisher}${url}${original}`),
  ]);
}

function apaCitation(work: Work, narrative: boolean): CitationParts {
  const spot = pinpoint(work, "apa");
  const tail = [apaYearText(work), spot].filter(Boolean).join(", ");
  const who = apaInTextAuthors([AUTHOR], narrative);
  return [plain(narrative ? `${who} (${tail})` : `(${who}, ${tail})`)];
}

function apaOutput(work: Work, labels: ShakespeareLabels): CitationOutput {
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(work) },
      { label: labels.parenthetical, parts: apaCitation(work, false) },
      { label: labels.narrative, parts: apaCitation(work, true) },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

function mlaEditors(work: Work): string {
  return work.editors.length
    ? mlaContributors({ editors: work.editors, translators: [] })
    : "";
}

function mlaWorksCited(work: Work): CitationParts {
  const title = titleCase(work.title);
  const lead = `${mlaAuthors([AUTHOR])}. `;
  const publisher = work.publisher ? publisherName(work.publisher, "mla") : "";
  const link = work.url ? mlaUrl(work.url) : "";
  if (work.book === "collected") {
    const pages = work.pages ? mlaPageLocator(work.pages) : "";
    const tail = [
      lowerFirst(mlaEditors(work)),
      work.edition,
      work.volume ? `vol. ${work.volume}` : "",
      publisher,
      work.year,
      pages,
      link,
    ]
      .filter(Boolean)
      .join(", ");
    return tidyParts([
      plain(lead),
      italic(title),
      plain(`${closer(title)} `),
      italic(titleCase(work.collection)),
      plain(`${tail ? `, ${tail}` : ""}.`),
    ]);
  }
  const tail = [
    mlaEditors(work),
    work.edition,
    work.series,
    publisher,
    work.year,
    link,
  ]
    .filter(Boolean)
    .join(", ");
  return tidyParts([
    plain(lead),
    italic(title),
    plain(`${closer(title)}${tail ? ` ${withPeriod(tail)}` : ""}`),
  ]);
}

function mlaInText(work: Work, labels: ShakespeareLabels) {
  const spot = pinpoint(work, "mla");
  if (work.abbreviation && work.kind === "play") {
    return {
      label: labels.inText,
      parts: [
        plain("("),
        italic(work.abbreviation),
        plain(`${spot ? ` ${spot}` : ""})`),
      ],
    };
  }
  const who = mlaInTextAuthors([AUTHOR]);
  const glue = work.kind === "sonnets" ? ", " : " ";
  return {
    label: labels.inText,
    parts: [plain(`(${who}${spot ? `${glue}${spot}` : ""})`)],
  };
}

function mlaOutput(work: Work, labels: ShakespeareLabels): CitationOutput {
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: mlaWorksCited(work) },
      mlaInText(work, labels),
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function chicagoImprint(work: Work): string {
  const publisher = work.publisher
    ? publisherName(work.publisher, "chicago")
    : "";
  return [publisher, dateOf(work.year) || "n.d."].filter(Boolean).join(", ");
}

function chicagoNote(work: Work, title: string): CitationParts {
  const editors = chicagoNoteContributors({
    editors: work.editors,
    translators: [],
  });
  const spot = pinpoint(work, "chicago");
  const person = naturalName(AUTHOR);
  const volume =
    work.book === "collected" && work.volume ? `vol. ${work.volume}` : "";
  const book = [editors, work.edition, volume].filter(Boolean).join(", ");
  const tail = [spot, work.url].filter(Boolean).join(", ");
  const closing =
    work.kind === "play" && spot
      ? " References are to act, scene, and line."
      : "";
  if (work.book === "collected") {
    return tidyParts([
      plain(`${person}, `),
      italic(title),
      plain(", in "),
      italic(titleCase(work.collection)),
      plain(
        `${book ? `, ${book}` : ""} (${chicagoImprint(work)})${tail ? `, ${tail}` : ""}.${closing}`
      ),
    ]);
  }
  return tidyParts([
    plain(`${person}, `),
    italic(title),
    plain(
      `${book ? `, ${book}` : ""} (${chicagoImprint(work)})${tail ? `, ${tail}` : ""}.${closing}`
    ),
  ]);
}

function chicagoShortNote(work: Work, title: string): CitationParts {
  const spot = pinpoint(work, "chicago");
  return tidyParts([
    plain(`${chicagoShortAuthors([AUTHOR])}, `),
    italic(title),
    plain(`${spot ? `, ${spot}` : ""}.`),
  ]);
}

function chicagoEditedBy(work: Work): string {
  const text = chicagoBibliographyContributors({
    editors: work.editors,
    translators: [],
  });
  return text.replace(RE_TRAILING_PERIOD, "");
}

function chicagoBibliography(work: Work, title: string): CitationParts {
  const link = work.url ? ` ${work.url}.` : "";
  const imprint = withPeriod(chicagoImprint(work));
  if (work.book === "collected") {
    const pages = work.pages ? pagesRange(work.pages, "chicago") : "";
    const inner = [
      work.editors.length ? lowerFirst(chicagoEditedBy(work)) : "",
      work.edition,
      work.volume ? `vol. ${work.volume}` : "",
      pages,
    ]
      .filter(Boolean)
      .join(", ");
    return tidyParts([
      plain("Shakespeare, William. "),
      italic(title),
      plain(`${closer(title)} In `),
      italic(titleCase(work.collection)),
      plain(`${inner ? `, ${withPeriod(inner)}` : "."} ${imprint}${link}`),
    ]);
  }
  const editors = work.editors.length ? `${chicagoEditedBy(work)}.` : "";
  const rest = [
    editors,
    work.edition ? withPeriod(work.edition) : "",
    work.series ? withPeriod(work.series) : "",
    imprint,
  ]
    .filter(Boolean)
    .join(" ");
  return tidyParts([
    plain("Shakespeare, William. "),
    italic(title),
    plain(`${closer(title)} ${rest}${link}`),
  ]);
}

function chicagoOutput(work: Work, labels: ShakespeareLabels): CitationOutput {
  const title = titleCase(work.title);
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoNote(work, title) },
      { label: labels.shortNote, parts: chicagoShortNote(work, title) },
      { label: labels.bibliography, parts: chicagoBibliography(work, title) },
    ],
    note: labels.chicagoNote,
  };
}

export function shakespeareIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function formatShakespeare(
  kind: ShakespeareWork,
  book: ShakespeareBook,
  values: MediaValues,
  labels: ShakespeareLabels
): CitationOutput[] {
  const work = readWork(kind, book, values);
  return [
    apaOutput(work, labels),
    mlaOutput(work, labels),
    chicagoOutput(work, labels),
  ];
}
